import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import API from "../api/api";
import {
  Search,
  Sun,
  Moon,
  Bell,
  BellOff,
  CheckCheck,
  ChevronRight,
  Loader2,
  LogOut,
  ChevronDown,
  Menu,
  User,
  ShieldCheck,
  X,
  Copy,
  Check,
  Settings as SettingsIcon,
} from "lucide-react";

export default function Topbar({ toggleMobileSidebar }) {
  const getStoredAdmin = () => {
    try {
      const raw = localStorage.getItem("admin");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileDetails, setProfileDetails] = useState(null);
  const [copiedId, setCopiedId] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [admin, setAdmin] = useState(() => getStoredAdmin());
  const notifRef = useRef(null);
  const msgRef = useRef(null);
  const profileRef = useRef(null);
  const searchRef = useRef(null);

  const getInitials = (name) => {
    if (!name) return "AS";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (msgRef.current && !msgRef.current.contains(event.target)) {
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("admin");
    navigate("/login", { replace: true });
  };

  const fetchAlerts = async () => {
    try {
      const response = await API.get("/admin/notifications/alerts", {
        params: { limit: 8 },
      });
      setNotifications(response.data?.alerts || []);
      setUnreadCount(response.data?.unreadCount || 0);
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    fetchAlerts();
    const intervalId = setInterval(fetchAlerts, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const markAllRead = async () => {
    try {
      await API.patch("/admin/notifications/mark-all-read");
      fetchAlerts();
    } catch {
      // ignore UI error to keep topbar non-blocking
    }
  };

  const markRead = async (id) => {
    try {
      await API.patch(`/admin/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((item) =>
          item._id === id ? { ...item, isRead: true } : item,
        ),
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch {
      // ignore UI error to keep topbar non-blocking
    }
  };

  useEffect(() => {
    const trimmed = searchQuery.trim();

    if (trimmed.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    let active = true;
    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const { data } = await API.get("/admin/search", {
          params: {
            q: trimmed,
            limit: 4,
          },
        });

        if (!active) return;
        setSearchResults(data?.results || []);
        setShowSearchResults(true);
      } catch {
        if (!active) return;
        setSearchResults([]);
      } finally {
        if (active) {
          setIsSearching(false);
        }
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [searchQuery]);

  const handleSearchSelect = (item) => {
    if (!item?.route) return;

    navigate(item.route, {
      state: {
        globalSearch: searchQuery.trim(),
        searchItemId: item.id,
      },
    });
    setShowSearchResults(false);
    setSearchFocused(false);
  };

  const handleSearchSubmit = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();

    if (searchResults.length > 0) {
      handleSearchSelect(searchResults[0]);
      return;
    }

    navigate("/admin/users", {
      state: {
        globalSearch: searchQuery.trim(),
      },
    });
    setShowSearchResults(false);
  };

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const res = await API.get("/admin/auth/me");

        if (res.data?.success) {
          setAdmin(res.data.admin || null);
          localStorage.setItem("admin", JSON.stringify(res.data.admin || {}));
        }
      } catch (error) {
        const storedAdmin = getStoredAdmin();
        if (storedAdmin) {
          setAdmin(storedAdmin);
        }
        console.log("Admin fetch error:", error);
      }
    };

    fetchAdmin();
  }, []);

  const fetchFullProfile = async () => {
    try {
      const res = await API.get("/admin/settings/profile");
      if (res.data?.success && res.data.admin) {
        setProfileDetails(res.data.admin);
      }
    } catch {
      // fallback
    }
  };

  useEffect(() => {
    if (showProfileModal) {
      fetchFullProfile();
    }
  }, [showProfileModal]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowProfileModal(false);
      }
    };
    if (showProfileModal) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showProfileModal]);

  const toRelativeTime = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Just now";

    const diffMs = Date.now() - date.getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    return `${days} day ago`;
  };

  // const messages = [
  //   {
  //     id: 1,
  //     user: "Emma Watson",
  //     avatar: "EW",
  //     msg: "Hey! Are you free tonight?",
  //     time: "Just now",
  //     online: true,
  //   },
  //   {
  //     id: 2,
  //     user: "John Doe",
  //     avatar: "JD",
  //     msg: "That sounds great! Let's meet up",
  //     time: "5 min ago",
  //     online: true,
  //   },
  //   {
  //     id: 3,
  //     user: "Sarah Connor",
  //     avatar: "SC",
  //     msg: "Thanks for the lovely evening!",
  //     time: "1 hr ago",
  //     online: false,
  //   },
  // ];

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between h-14 xl:h-16 px-3 sm:px-4 lg:px-6 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
      {/* Left Section */}
      <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
        {/* Mobile Menu Toggle */}
        <button
          onClick={toggleMobileSidebar}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        >
          <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>

        {/* Search Bar */}
        <div
          ref={searchRef}
          className={`relative transition-all duration-300 ${searchFocused ? "w-48 sm:w-64 lg:w-80" : "w-32 sm:w-48 lg:w-64"}`}
        >
          <Search className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            placeholder="Search users, pages, offers..."
            onFocus={() => {
              setSearchFocused(true);
              if (searchQuery.trim().length >= 2) {
                setShowSearchResults(true);
              }
            }}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={handleSearchSubmit}
            className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 xl:py-2.5 rounded-lg bg-gray-100 dark:bg-slate-700 border-0 text-xs sm:text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black/20 transition-all"
          />
          {/* <kbd className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 hidden lg:inline-flex items-center gap-1 px-1.5 xl:px-2 py-0.5 text-[10px] xl:text-xs text-gray-400 bg-gray-200 dark:bg-slate-600 rounded">
            ⌘K
          </kbd> */}

          {showSearchResults && (
            <div className="absolute top-full mt-2 w-full min-w-[280px] bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-40">
              <div className="px-3 py-2 text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                Global Admin Search
              </div>

              {isSearching && (
                <div className="flex items-center gap-2 px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Searching...
                </div>
              )}

              {!isSearching && searchQuery.trim().length < 2 && (
                <div className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
                  Type at least 2 characters
                </div>
              )}

              {!isSearching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                <div className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
                  No matching records found
                </div>
              )}

              {!isSearching && searchResults.length > 0 && (
                <div className="max-h-80 overflow-y-auto">
                  {searchResults.map((item) => (
                    <button
                      key={`${item.type}-${item.id}`}
                      type="button"
                      onClick={() => handleSearchSelect(item)}
                      className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                          {item.title}
                        </p>
                        <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-300 whitespace-nowrap">
                          {item.type}
                        </span>
                      </div>
                      <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {item.subtitle}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-1 sm:gap-2">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="relative p-2 xl:p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-all duration-300 group"
        >
          {theme === "dark" ? (
            <Sun className="w-4 h-4 xl:w-5 xl:h-5 text-amber-500 group-hover:rotate-90 transition-transform duration-300" />
          ) : (
            <Moon className="w-4 h-4 xl:w-5 xl:h-5 text-gray-700 group-hover:-rotate-12 transition-transform duration-300" />
          )}
        </button>


        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowProfile(false);
            }}
            aria-label="Notifications"
            className={`relative p-2 xl:p-2.5 rounded-lg transition-all duration-200 group ${
              showNotifications
                ? "bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white"
                : "hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-300"
            }`}
          >
            <span className="relative inline-flex items-center justify-center">
              <Bell className="w-4 h-4 xl:w-5 xl:h-5 transition-transform duration-200 group-hover:rotate-12" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 -translate-y-1/3 translate-x-1/2 min-w-[18px] h-[18px] xl:min-w-[20px] xl:h-[20px] px-1 flex items-center justify-center text-[9px] xl:text-[10px] font-bold leading-none text-white bg-gradient-to-r from-rose-500 to-pink-500 rounded-full ring-2 ring-white dark:ring-slate-800 shadow-sm shadow-rose-500/30">
                  {unreadCount > 99 ? "20+" : unreadCount}
                </span>
              )}
            </span>
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700/80 overflow-hidden animate-slideInUp z-50">
              {/* Dropdown Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700/80 bg-gray-50/60 dark:bg-slate-800/60 backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm text-gray-800 dark:text-gray-100">
                    Notifications
                  </h3>
                  {unreadCount > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200/60 dark:border-rose-900/40">
                      {unreadCount > 99 ? "99+" : unreadCount} new
                    </span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 transition-colors cursor-pointer"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Mark all read
                  </button>
                )}
              </div>

              {/* Notification List */}
              <div className="max-h-72 sm:max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-700/50">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center flex flex-col items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-700/80 flex items-center justify-center text-gray-400 dark:text-gray-500 mb-2.5">
                      <BellOff className="w-5 h-5" />
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200">
                      No notifications yet
                    </p>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                      You are all caught up!
                    </p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif._id}
                      onClick={() => !notif.isRead && markRead(notif._id)}
                      className={`group flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/60 cursor-pointer transition-colors ${
                        !notif.isRead
                          ? "bg-rose-50/40 dark:bg-rose-950/20"
                          : ""
                      }`}
                    >
                      <div className="mt-1 flex-shrink-0">
                        {!notif.isRead ? (
                          <span className="flex h-2 w-2 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
                          </span>
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-slate-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p
                            className={`text-xs sm:text-sm truncate ${
                              !notif.isRead
                                ? "font-semibold text-gray-900 dark:text-white"
                                : "font-medium text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {notif.title}
                          </p>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
                            {toRelativeTime(notif.createdAt)}
                          </span>
                        </div>
                        <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">
                          {notif.message}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Dropdown Footer */}
              <div className="px-4 py-2.5 bg-gray-50/70 dark:bg-slate-800/80 border-t border-gray-100 dark:border-slate-700/80">
                <button
                  onClick={() => {
                    setShowNotifications(false);
                    navigate("/admin/notifications");
                  }}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:text-rose-600 dark:hover:text-rose-400 transition-colors cursor-pointer"
                >
                  <span>View All Notifications</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider - Hidden on mobile */}
        <div className="hidden sm:block w-px h-5 xl:h-6 bg-gray-200 dark:bg-gray-700 mx-0.5 xl:mx-1" />

        {/* Profile Dropdown */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => {
              setShowProfile(!showProfile);
            }}
            className="flex items-center gap-2 xl:gap-3 p-1 xl:p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          >
            <div className="relative">
              <div className="w-8 h-8 xl:w-9 xl:h-9 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center ring-2 ring-white dark:ring-slate-700">
                <span className="text-white font-semibold text-xs xl:text-sm">
                  {getInitials(admin?.name)}
                </span>
              </div>
              <span className="absolute bottom-0 right-0 w-2 h-2 xl:w-2.5 xl:h-2.5 bg-green-500 rounded-full ring-2 ring-white dark:ring-slate-800" />
            </div>
            <div className="hidden md:block text-left">
              <p className="text-xs xl:text-sm font-medium text-gray-800 dark:text-gray-200">
                {admin?.name || "Admin"}
              </p>
              <p className="text-[10px] xl:text-xs text-gray-500 dark:text-gray-400">
                {admin?.role || "Admin"}
              </p>
            </div>
            <ChevronDown
              className={`hidden md:block w-3 h-3 xl:w-4 xl:h-4 text-gray-400 transition-transform duration-200 ${showProfile ? "rotate-180" : ""}`}
            />
          </button>

          {showProfile && (
            <div className="absolute right-0 top-full mt-2 w-52 sm:w-60 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-slideInUp z-40">
              <div className="px-3.5 sm:px-4 py-2.5 sm:py-3 border-b border-gray-100 dark:border-gray-700/80 bg-gray-50/60 dark:bg-slate-700/30">
                <p className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                  {admin?.name || "Admin"}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                  {admin?.email || "admin@example.com"}
                </p>
              </div>

              <div className="py-1">
                <button
                  type="button"
                  onClick={() => {
                    setShowProfile(false);
                    setShowProfileModal(true);
                  }}
                  className="flex items-center gap-2.5 w-full px-3.5 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-200 hover:bg-pink-50 dark:hover:bg-pink-500/10 hover:text-pink-600 dark:hover:text-pink-400 transition-colors cursor-pointer text-left"
                >
                  <User className="w-4 h-4 text-pink-500" />
                  <span className="font-medium">View Profile</span>
                </button>
              </div>

              <div className="py-1 border-t border-gray-100 dark:border-gray-700/80">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-2.5 w-full px-3.5 sm:px-4 py-2 text-xs sm:text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors cursor-pointer text-left"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Admin Profile Details Modal */}
      {showProfileModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
          onClick={() => setShowProfileModal(false)}
        >
          <div
            className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden transform transition-all animate-scaleUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient banner */}
            <div className="relative h-28 bg-gradient-to-r from-pink-500 via-rose-500 to-purple-600 p-4">
              <button
                type="button"
                onClick={() => setShowProfileModal(false)}
                className="absolute top-3 right-3 p-1.5 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors cursor-pointer"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Profile Avatar & Primary Info */}
            <div className="relative px-6 pb-6 pt-0">
              <div className="flex items-end justify-between -mt-12 mb-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-700 text-white flex items-center justify-center font-bold text-2xl shadow-xl ring-4 ring-white dark:ring-slate-800">
                    {getInitials(profileDetails?.name || admin?.name)}
                  </div>
                  <span
                    className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full ring-4 ring-white dark:ring-slate-800 flex items-center justify-center"
                    title="Active Admin"
                  >
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400 border border-pink-200/60 dark:border-pink-900/40">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {profileDetails?.role || admin?.role || "ADMIN"}
                  </span>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {profileDetails?.name || admin?.name || "Admin"}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {profileDetails?.email || admin?.email || "admin@example.com"}
                </p>
              </div>

              {/* Details List */}
              <div className="mt-5 space-y-2.5">
                <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Role & Access</span>
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    Full Platform Access
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Account Status</span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-600 dark:text-green-400">
                    <span className="w-2 h-2 rounded-full bg-green-500" /> Active
                  </span>
                </div>

                {(profileDetails?.id || admin?.id || admin?._id) && (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-700">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Admin ID</span>
                    <button
                      type="button"
                      onClick={() => {
                        const id = profileDetails?.id || admin?.id || admin?._id;
                        if (id) {
                          navigator.clipboard.writeText(String(id));
                          setCopiedId(true);
                          setTimeout(() => setCopiedId(false), 2000);
                        }
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-mono text-gray-700 dark:text-gray-300 hover:text-pink-600 dark:hover:text-pink-400 transition-colors cursor-pointer"
                      title="Click to copy Admin ID"
                    >
                      <span>
                        {String(profileDetails?.id || admin?.id || admin?._id).slice(0, 10)}...
                      </span>
                      {copiedId ? (
                        <Check className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </button>
                  </div>
                )}

                {profileDetails?.createdAt && (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-700">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Member Since</span>
                    <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                      {new Date(profileDetails.createdAt).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions Footer */}
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowProfileModal(false);
                    navigate("/admin/settings");
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs sm:text-sm font-semibold hover:from-pink-600 hover:to-rose-600 shadow-md shadow-pink-500/20 transition-all cursor-pointer"
                >
                  <SettingsIcon className="w-4 h-4" />
                  Account Settings
                </button>
                <button
                  type="button"
                  onClick={() => setShowProfileModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
